import { Head, router, useForm } from "@inertiajs/react"

import AccountForm, {
  type BusinessProfileForm,
  DEFAULT_HOURS,
} from "@/components/accounts/account-form"
import type { PlacesPickerSelection } from "@/components/places-picker"
import RecordForm from "@/components/record-form"
import RecordPageHeader from "@/components/ui/record-page-header"
import AdminLayout from "@/layouts/admin-layout"
import {
  adminAccountsPath,
  businessDetailsAdminAccountsPath,
  businessSearchAdminAccountsPath,
  newAdminAccountPath,
} from "@/routes"
import type { Account, BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Accounts",
    href: adminAccountsPath(),
  },
  {
    title: "New Account",
    href: newAdminAccountPath(),
  },
]

interface Partner {
  uid: string
  name: string
}

interface BusinessCategoryOption {
  value: string
  label: string
}

export default function NewAccount({
  account,
  partners,
  business_categories,
}: {
  account: Account
  partners: Partner[]
  business_categories: BusinessCategoryOption[]
}) {
  const { data, setData, post, processing, errors } = useForm({
    account: {
      name: account.name,
      logo: null as File | null,
      state: account.state,
      partner_uid: "",
      demo_mode: false,
      landing_page_cta_enabled: true,
      website_selection: null as PlacesPickerSelection | null,
      business_profile: {
        business_name: "",
        phone: "",
        address: "",
        website: "",
        description: "",
        services: "",
        business_category: "",
        hours: DEFAULT_HOURS,
        hours_status: "open_no_hours",
      } as BusinessProfileForm,
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(adminAccountsPath(), {
      forceFormData: true,
    })
  }

  function handleCancel() {
    router.visit(adminAccountsPath())
  }

  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />
      <div className="flex h-full w-full flex-col gap-6 p-4">
        <div className="mx-auto w-full max-w-2xl">
          <RecordPageHeader title="New Account" />
        </div>
        <RecordForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          processing={processing}
        >
          <AccountForm
            data={data}
            setData={setData}
            errors={errors}
            logoUrl={account.logo_url ?? null}
            partners={partners}
            businessCategories={business_categories}
            searchPath={businessSearchAdminAccountsPath()}
            detailsPath={businessDetailsAdminAccountsPath()}
          />
        </RecordForm>
      </div>
    </AdminLayout>
  )
}
